import { useCallback, useState } from 'react'
import { X, AlertTriangle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { apiError } from '../lib/api'

// Diálogo de confirmação genérico. Quando `confirmWord` é passado, o botão de
// confirmar só habilita depois que o usuário digita exatamente a palavra (ex.: o
// vmc-id, para ações irreversíveis). Cancelar é o botão default (autoFocus).
// Visual baseado no DeviceActionsModal; botão perigoso em volis-red.
export default function ConfirmDialog({ title, message, confirmWord, confirmLabel, danger = false, onConfirm, onClose }) {
  const { t } = useTranslation()
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const needsWord = !!confirmWord
  const blocked = needsWord && typed.trim() !== confirmWord

  const submit = async () => {
    if (blocked) return
    setBusy(true)
    setError(null)
    try {
      await onConfirm()
      onClose()
    } catch (err) {
      setError(err.message || String(err))
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-start justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-start gap-2.5">
            {danger && <AlertTriangle size={18} className="text-volis-red mt-0.5 shrink-0" />}
            <h2 className="font-semibold text-slate-800 dark:text-slate-100">{title}</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300">
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          {message && <p className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-line">{message}</p>}

          {needsWord && (
            <div className="space-y-1.5">
              <label className="block text-xs text-slate-500 dark:text-slate-400">
                {t('debug:confirm.type_to_enable', { word: confirmWord })}
              </label>
              <input
                type="text"
                value={typed}
                onChange={e => setTyped(e.target.value)}
                placeholder={confirmWord}
                spellCheck={false}
                autoComplete="off"
                className="w-full h-9 px-2.5 text-sm font-mono rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-volis-red/30"
              />
            </div>
          )}

          {error && <p className="text-xs text-volis-red">{error}</p>}

          <div className="flex justify-end gap-2 pt-1">
            <button
              onClick={onClose}
              autoFocus
              disabled={busy}
              className="text-sm font-medium px-3 py-1.5 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50"
            >
              {t('common:actions.cancel')}
            </button>
            <button
              onClick={submit}
              disabled={busy || blocked}
              className={`text-sm font-semibold px-3 py-1.5 rounded-md text-white disabled:bg-slate-200 disabled:text-slate-400 dark:disabled:bg-slate-700 dark:disabled:text-slate-500 ${
                danger ? 'bg-volis-red hover:brightness-95' : 'bg-brand hover:bg-brand-700'
              }`}
            >
              {busy ? t('debug:confirm.working') : (confirmLabel || t('common:actions.confirm'))}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// useConfirm: um só caminho para ação destrutiva na app. Devolve [dialog, ask]:
// renderize `dialog` no fim da tela e chame ask({title, message, danger, confirmWord,
// confirmLabel, onConfirm}). O onConfirm que lançar erro mantém o diálogo aberto e
// mostra a mensagem, então o erro do servidor nunca passa despercebido.
export function useConfirm() {
  const [req, setReq] = useState(null)
  const ask = useCallback((cfg) => setReq(cfg), [])
  const dialog = req ? <ConfirmDialog {...req} onClose={() => setReq(null)} /> : null
  return [dialog, ask]
}

// Helper das listas: DELETE que levanta o corpo do erro, para o diálogo mostrar.
export async function deleteJSON(url) {
  const res = await fetch(url, { method: 'DELETE' })
  if (!res.ok) throw await apiError(res)
  return res.json().catch(() => null)
}
